import React, { useEffect, useState } from "react";
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	Tooltip,
	ResponsiveContainer,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { FaFire, FaBook, FaStar } from "react-icons/fa";
import { useAuth } from "../hooks/useAuth";
import { useServer } from "../hooks/useServer";
import { useLoading } from "../hooks/LoadingProvider";

import {
	FiSettings,
	FiLogOut,
	FiUser,
	FiChevronLeft,
	FiMenu,
} from "react-icons/fi";

import { motion } from "framer-motion";

interface HeaderProps {
	exercise?: string;
	back?: () => void;
}

const Header: React.FC<HeaderProps> = ({ exercise, back }) => {
	const { currentUser, logout } = useAuth();
	const navigate = useNavigate();
	const [showMenu, setShowMenu] = useState(false);

	useEffect(() => {
		const handleClickOutside = (event: any) => {
			if (showMenu && event.target && !event.target.closest(".profile-menu")) {
				setShowMenu(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [showMenu]);

	return (
		<header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-600 to-blue-800 shadow-lg text-white">
			<div className="max-w-7xl mx-auto px-4 py-3">
				<div className="flex items-center justify-between">
					{/* Back Button */}
					<button
						onClick={() => back && back()}
						className="flex items-center space-x-2 hover:bg-white/10 rounded-lg px-3 py-2 transition-colors"
					>
						<FiChevronLeft className="text-xl" />
						<span className="font-medium hidden sm:inline">Back</span>
					</button>

					{/* Name */}
					<h1 className="text-lg sm:text-2xl font-semibold truncate">
						{currentUser?.displayName || "Profile"}
					</h1>

					{/* Profile Menu */}
					<div className="relative profile-menu">
						<button
							onClick={() => setShowMenu(!showMenu)}
							className="flex items-center space-x-2 focus:outline-none hover:opacity-80 transition-opacity"
						>
							<div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden border-2 border-white/20">
								{currentUser?.photoURL ? (
									<img
										src={currentUser?.photoURL}
										alt={currentUser?.displayName || "User"}
										className="w-full h-full object-cover"
									/>
								) : (
									<div className="w-full h-full bg-white/10 flex items-center justify-center">
										<span className="text-lg font-semibold">
											{(currentUser?.displayName || "U").charAt(0)}
										</span>
									</div>
								)}
							</div>
						</button>

						{showMenu && (
							<div className="absolute right-0 mt-3 w-56 bg-white rounded-xl shadow-xl py-2 text-gray-700 border border-gray-100">
								<div className="px-4 py-2 border-b border-gray-100">
									<p className="font-medium text-gray-900">
										{currentUser?.displayName}
									</p>
									<p className="text-sm text-gray-500">{currentUser?.email}</p>
								</div>
								<button
									className="w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-center transition-colors"
									onClick={() => {
										navigate("/profile");
										setShowMenu(false);
									}}
								>
									<FiUser className="mr-3 text-gray-400" />
									Profile
								</button>
								<button
									className="w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-center transition-colors"
									onClick={() => {
										navigate("/settings");
										setShowMenu(false);
									}}
								>
									<FiSettings className="mr-3 text-gray-400" />
									Settings
								</button>
								<button
									className="w-full px-4 py-2.5 text-left hover:bg-red-50 flex items-center text-red-600 transition-colors"
									onClick={() => logout()}
								>
									<FiLogOut className="mr-3" />
									Logout
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</header>
	);
};

const Profile = () => {
	const { isAuthenticated } = useAuth();
	const { user, getUserProfile } = useServer();
	const navigate = useNavigate();
	const { showLoading, hideLoading } = useLoading();

	useEffect(() => {
		if (!user) {
			getUserProfile();
		}
	}, [user]);

	// Get top 5 strongest and weakest phonemes
	const getPhonemeStats = () => {
		if (
			!user?.scores ||
			!Object.entries(user.scores).length ||
			!user.scores.pronunciation ||
			!Object.entries(user.scores.pronunciation).length
		) {
			return { strongest: [], weakest: [] };
		}

		const phonemes = Object.entries(user.scores.pronunciation)
			.filter(([key]) => key !== "overall")
			.map(([key, value]: [string, any]) => ({
				phoneme: key,
				score: value.avg,
			}))
			.sort((a, b) => b.score - a.score);

		return {
			strongest: phonemes.slice(0, 5),
			weakest: phonemes.slice(-5).reverse(),
		};
	};

	const { strongest, weakest } = getPhonemeStats();

	// Calculate roleplay progress
	const roleplayProgress = ((user?.roleplayIndex || 0) / 100) * 100; // Assuming 100 is the target

	// Get last 5 learned words
	const recentWords = user?.vocabulary?.slice(-5).reverse() || [];

	const ScoreGraph = () => {
		if (!user?.scores?.overall || user?.scores?.overall?.length < 1) {
			return null;
		}

		const chartData = user.scores.overall.map((item: any) => ({
			timestamp: new Date(item.timestamp).toLocaleTimeString(),
			avg: item.avg,
		}));

		return (
			<div className="bg-white p-4 rounded-lg">
				<h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">
					Overall Pronunciation Score
				</h3>
				<div className="h-[200px] w-full">
					<ResponsiveContainer width="100%" height="100%">
						<LineChart data={chartData}>
							<XAxis dataKey="timestamp" tick={{ fontSize: 12 }} />
							<YAxis
								domain={[85, 87]}
								tickFormatter={(value: number) => `${value}%`}
								tick={{ fontSize: 12 }}
							/>
							<Tooltip
								formatter={(value: number) => [`${value.toFixed(2)}%`, "Score"]}
								labelStyle={{ color: "#666" }}
								contentStyle={{
									backgroundColor: "white",
									border: "1px solid #ccc",
									borderRadius: "4px",
									padding: "8px",
								}}
							/>
							<Line
								type="monotone"
								dataKey="avg"
								stroke="#2563eb"
								strokeWidth={2}
								dot={{ fill: "#2563eb", r: 4 }}
							/>
						</LineChart>
					</ResponsiveContainer>
				</div>
			</div>
		);
	};

	useEffect(() => {
		if (user && !user.interests) {
			//navigate("/onboarding");
		}
	}, [user]);

	return (
		<div className="min-h-screen bg-gray-50">
			<Header exercise="Profile" back={() => navigate("/liveTutor")} />

			<main className="container mx-auto px-4 pt-24 pb-12">
				{/* Streak Display */}
				<div className="mb-8 bg-white rounded-2xl shadow-lg overflow-hidden">
					<div className="relative max-w-lg mx-auto p-8 flex flex-col items-center">
						<div className="relative">
							<img
								src="fire-flame.gif"
								alt="Fire"
								className="w-32 h-auto mb-4"
							/>
							<div className="absolute top-14 left-1/2 transform -translate-x-1/2">
								<span className="text-6xl font-bold text-gray-800 drop-shadow-lg">
									{user?.gamification?.streak || 0}
								</span>
							</div>
						</div>
						<p className="text-lg text-center font-medium text-gray-700 mt-2">
							You're on a hot streak! Keep up the great work!
						</p>
					</div>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* Level Progress Section */}
					<section className="bg-white rounded-2xl shadow-lg p-6">
						<h2 className="text-2xl font-bold mb-6">Level Progress</h2>

						{/* XP Progress Bar */}
						<div className="mb-8">
							<div className="flex justify-between text-sm text-gray-600 mb-2">
								<span>
									Current Level: {Math.floor((user?.xp || 0) / 1000) + 1}
								</span>
								<span>
									{user?.xp || 0} / {Math.ceil((user?.xp || 0) / 1000) * 1000}{" "}
									XP
								</span>
							</div>
							<div className="relative h-4 bg-gray-100 rounded-full">
								<div
									className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-500"
									style={{
										width: `${((user?.xp || 0) % 1000) / 10}%`,
									}}
								/>

								{/* Level Markers */}
								<div className="absolute top-0 left-0 w-full h-full flex justify-between items-center px-2">
									{[1, 2, 3, 4].map((level) => (
										<div
											key={level}
											className={`w-3 h-3 rounded-full border-2 ${
												level <= Math.floor((user?.xp || 0) / 1000) + 1
													? "bg-white border-purple-500"
													: "bg-gray-200 border-gray-300"
											}`}
										/>
									))}
								</div>
							</div>
						</div>

						{/* Rewards Display - Horizontal Scrollable */}
						<div className="overflow-x-auto pb-4">
							<div className="flex gap-4 min-w-min">
								{[
									{ level: 5, reward: "🎭 New Tutor Unlock", icon: "👩‍🏫" },
									{ level: 10, reward: "🔓 Premium Tutor", icon: "👨‍🏫" },
									{ level: 15, reward: "⭐ Expert Tutor", icon: "👩‍🎓" },
									{ level: 20, reward: "🎉 2000 XP Bonus", icon: "🎯" },
								].map((item) => (
									<div
										key={item.level}
										className={`flex-none w-[180px] flex flex-col items-center p-4 rounded-lg transition-all duration-300 ${
											item.level <= Math.floor((user?.xp || 0) / 1000) + 1
												? "bg-blue-50 border-2 border-blue-200"
												: "bg-gray-50 border-2 border-gray-200 opacity-50"
										}`}
									>
										<div className="text-4xl mb-2">{item.icon}</div>
										<div className="font-semibold text-center mb-1">
											Level {item.level}
										</div>
										<div className="text-sm text-center text-gray-600">
											{item.reward}
										</div>
										{item.level <= Math.floor((user?.xp || 0) / 1000) + 1 && (
											<div className="mt-2 text-green-500 text-sm font-medium">
												Unlocked!
											</div>
										)}
									</div>
								))}
							</div>
						</div>
					</section>

					{/* Vocabulary Progress */}
					<section className="bg-white rounded-2xl shadow-lg p-6">
						<h2 className="text-2xl font-bold mb-4">
							<div className="flex items-center">
								<FaBook className="mr-2 text-blue-500" />
								Vocabulary Progress
							</div>
						</h2>
						<div className="mb-4">
							<div className="flex justify-between mb-2">
								<span>Words Learned</span>
								<span className="font-bold">
									{user?.vocabulary?.length || 0}/5000
								</span>
							</div>
							<div className="h-2 bg-gray-200 rounded-full">
								<motion.div
									className="h-full bg-blue-500 rounded-full"
									initial={{ width: 0 }}
									animate={{
										width: `${((user?.vocabulary?.length || 0) / 5000) * 100}%`,
									}}
									transition={{ duration: 1 }}
								/>
							</div>
						</div>
						<div>
							<h3 className="font-semibold mb-2">Recently Learned Words</h3>
							<div className="grid grid-cols-2 gap-2">
								{recentWords.map((word: string, index: number) => (
									<div
										key={index}
										className="bg-gray-50 rounded-lg p-2 text-sm"
									>
										{word}
									</div>
								))}
							</div>
						</div>
					</section>

					{/* Phoneme Analysis */}
					<section className="bg-white rounded-2xl shadow-lg p-6">
						<h2 className="text-2xl font-bold mb-4">Phoneme Analysis</h2>
						<div className="grid grid-cols-2 gap-6">
							<div>
								<h3 className="font-semibold text-green-600 mb-3">
									Strongest Sounds
								</h3>
								{strongest.length > 0 ? (
									strongest.map((item, index) => (
										<div key={index} className="mb-2">
											<div className="flex justify-between mb-1">
												<span>{item.phoneme}</span>
												<span>{Math.round(item.score)}%</span>
											</div>
											<div className="h-2 bg-gray-200 rounded-full">
												<div
													className="h-full bg-green-500 rounded-full"
													style={{ width: `${item.score}%` }}
												/>
											</div>
										</div>
									))
								) : (
									<div className="text-gray-500">No data available</div>
								)}
							</div>
							<div>
								<h3 className="font-semibold text-red-600 mb-3">
									Sounds to Improve
								</h3>
								{weakest.length > 0 ? (
									weakest.map((item, index) => (
										<div key={index} className="mb-2">
											<div className="flex justify-between mb-1">
												<span>{item.phoneme}</span>
												<span>{Math.round(item.score)}%</span>
											</div>
											<div className="h-2 bg-gray-200 rounded-full">
												<div
													className="h-full bg-red-500 rounded-full"
													style={{ width: `${item.score}%` }}
												/>
											</div>
										</div>
									))
								) : (
									<div className="text-gray-500">No data available</div>
								)}
							</div>
						</div>
					</section>

					{/* Teacher Feedback */}
					<section className="bg-white rounded-2xl shadow-lg p-6">
						<h2 className="text-2xl font-bold mb-4">
							<div className="flex items-center">
								<FaStar className="mr-2 text-yellow-500" />
								Teacher Feedback
							</div>
						</h2>
						<div className="text-lg text-gray-700 italic">
							"
							{user?.teacherfeedback ||
								"Keep practicing to get personalized feedback!"}
							"
						</div>
					</section>

					{/* Roleplay Progress */}
					<section className="bg-white rounded-2xl shadow-lg p-6 md:col-span-2">
						<h2 className="text-2xl font-bold mb-4">Roleplay Progress</h2>
						<div className="mb-4">
							<div className="flex justify-between mb-2">
								<span>Completed Roleplays</span>
								<span className="font-bold">{user?.roleplayIndex || 0}/∞</span>
							</div>
							<div className="h-3 bg-gray-200 rounded-full">
								<motion.div
									className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
									initial={{ width: 0 }}
									animate={{ width: `${roleplayProgress}%` }}
									transition={{ duration: 1 }}
								/>
							</div>
						</div>
					</section>

					{/* Achievements Section (Partially Blurred) */}
					<section className="relative bg-white rounded-2xl shadow-lg p-6 md:col-span-2">
						<h2 className="text-2xl font-bold mb-6 relative z-20">
							Achievements
						</h2>
						<div className="absolute inset-0 top-[60px] backdrop-blur-sm bg-white/10 z-10 rounded-b-2xl flex items-center justify-center">
							<span className="text-xl font-bold text-gray-800">
								Coming Soon
							</span>
						</div>
						<div className="grid grid-cols-3 gap-6">
							{/* Achievement placeholders */}
							{[1, 2, 3].map((i) => (
								<div
									key={i}
									className="flex flex-col items-center p-4 bg-gray-50 rounded-xl"
								>
									<div className="w-16 h-16 bg-gray-200 rounded-full mb-2" />
									<div className="h-4 w-24 bg-gray-200 rounded-full" />
								</div>
							))}
						</div>
					</section>
				</div>
			</main>
		</div>
	);
};

export default Profile;
